import {useEffect, useRef, useState, useMemo, useCallback} from 'react';
import {useDataProvider, useQuery} from "react-admin";
import {ExtendedDataProvider} from "@src/types";
import {useNotify} from "ra-core";
import {Modal, Input} from 'antd';
import {ModalWidth} from "@src/components/Common/Variables";
import {useLocation} from 'react-router-dom';
import React from 'react';

const KEY_STAGE: Record<0|1|2, string> = {0: "config", 1: "verify", 2: "execution"};

const RESOURCE_PACK_DATA = "serviceowner_pack_data";
const RESOURCE_PIPELINE = "serviceowner_pack_pipelines";
const RESOURCE_PACK = "serviceowner_pack";

export const getAIProcessorResponse = (pipeline: any, current: 0|1|2) => {
    if (current === 0) return pipeline?.ai_processor_response_config;
    if (current === 1) return pipeline?.ai_processor_response_verify;
    return pipeline?.ai_processor_response_execution;
};

const usePackPipeline = (service_item_id: number) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const location = useLocation();

    // Read version from URL parameter on component mount
    const getVersionFromURL = () => {
        const params = new URLSearchParams(location.search);
        const version = params.get('pipeline_version');
        if (version === 'latest' || version === null) {
            return "latest";
        }
        const versionNumber = parseInt(version, 10);
        return isNaN(versionNumber) ? "latest" : versionNumber;
    };

    const [current, setCurrent] = useState<0|1|2>(0);
    const [currentPipelineID, setCurrentPipelineID] = useState<"latest" | number>(getVersionFromURL());
    const [compareMode, setCompareMode] = useState(false);
    const [comparePipelineID, setComparePipelineID] = useState<"latest" | number>("latest");
    const [isEditing, setIsEditing] = useState(false);
    const isEditingRef = useRef(isEditing);

    const pipelineURLVerb = currentPipelineID === "latest"
        ? `latest/service_item/${service_item_id}`
        : `${currentPipelineID}`;
    const comparePipelineURLVerb = comparePipelineID === "latest"
        ? `latest/service_item/${service_item_id}`
        : `${comparePipelineID}`;

    const { data: dataPipeline, refetch: refetchPipeline } = useQuery({
        type: 'getAction',
        resource: RESOURCE_PIPELINE,
        payload: {id: undefined, verb: pipelineURLVerb, filter: {}}
    });

    const { data: dataComparePipeline, refetch: refetchComparePipeline } = useQuery({
        type: 'getAction',
        resource: RESOURCE_PIPELINE,
        payload: {id: undefined, verb: comparePipelineURLVerb, filter: {}},
    });

    const { data: dataVersions, refetch: refetchVersions } = useQuery({
        type: 'getAction',
        resource: RESOURCE_PIPELINE,
        payload: {id: undefined, verb: `versions/service_item/${service_item_id}`, filter: {}}
    });

    const refreshAll = useCallback(() => {
        refetchPipeline();
        refetchVersions();
        if (compareMode) {
            refetchComparePipeline();
        }
    }, [compareMode, refetchPipeline, refetchVersions, refetchComparePipeline]);

    const refreshAllRef = useRef(refreshAll);
    useEffect(() => { refreshAllRef.current = refreshAll; }, [refreshAll]);

    useEffect(() => {
        isEditingRef.current = isEditing;
    }, [isEditing]);

    // Convert URL version to pipeline ID once dataVersions is loaded (initial load only)
    const hasResolvedURLVersion = useRef(false);
    useEffect(() => {
        if (hasResolvedURLVersion.current) return;
        if (dataVersions) {
            hasResolvedURLVersion.current = true;
            const params = new URLSearchParams(location.search);
            const versionFromURL = params.get('pipeline_version');

            if (versionFromURL && versionFromURL !== 'latest') {
                const versionNumber = parseInt(versionFromURL, 10);
                if (!isNaN(versionNumber)) {
                    const entries = Object.entries(dataVersions);
                    const pipeline = entries.find(([key, item]: [string, any]) => item.version === versionNumber);
                    if (pipeline) {
                        const pipelineID = (pipeline[1] as any).id;
                        if (pipelineID !== currentPipelineID) {
                            setCurrentPipelineID(pipelineID);
                        }
                    }
                }
            }
        }
    }, [dataVersions]);

    // Polling — stable interval that always calls the latest refreshAll
    useEffect(() => {
        const interval = setInterval(() => {
            if (!isEditingRef.current)
                refreshAllRef.current();
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    // Derived data — replaces useState+useEffect pattern
    const currentPackData = useMemo(() => {
        if (!dataPipeline) return {};
        const dataMapper: Record<0|1|2, any> = {
            0: dataPipeline?.config,
            1: dataPipeline?.verify,
            2: dataPipeline?.execution
        };
        return dataMapper[current];
    }, [dataPipeline, current]);

    const comparePackData = useMemo(() => {
        if (!dataComparePipeline || !compareMode) return {};
        const dataMapper: Record<0|1|2, any> = {
            0: dataComparePipeline?.config,
            1: dataComparePipeline?.verify,
            2: dataComparePipeline?.execution
        };
        return dataMapper[current];
    }, [dataComparePipeline, compareMode, current]);

    const currentAIProcessorResponse = useMemo(
        () => getAIProcessorResponse(dataPipeline, current),
        [dataPipeline, current]
    );

    const compareAIProcessorResponse = useMemo(
        () => compareMode ? getAIProcessorResponse(dataComparePipeline, current) : null,
        [dataComparePipeline, compareMode, current]
    );

    const genUIData = useMemo(() => {
        const genUI = currentAIProcessorResponse?.body?.gen_ui;
        if (!genUI || typeof genUI !== 'object') return null;
        // Format 1: { components: [...] }
        if (genUI.components) return genUI.components;
        // Format 2: { component_0: {...}, component_1: {...}, ... }
        const entries = Object.entries(genUI);
        if (entries.length > 0 && entries.every(([, v]: [string, any]) => v && typeof v === 'object' && typeof v.type === 'string')) {
            return entries
                .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
                .map(([, v]) => v);
        }
        return null;
    }, [currentAIProcessorResponse]);

    const pipelineOptions = useMemo(() => {
        if (!dataVersions) return undefined;
        const entries = Object.entries(dataVersions);
        const latestVersion = Math.max(...entries.map(([key, item]: [string, any]) => item.version));
        return entries.map(([key, item]: [string, any]) => ({
            value: item.id,
            label: item.version === latestVersion ? `${item.version} (latest)` : `${item.version}`
        }));
    }, [dataVersions]);

    const getDisplayPipelineID = useCallback((pipelineID: "latest" | number) => {
        if (pipelineID === "latest" && dataVersions) {
            const entries = Object.entries(dataVersions);
            const latestVersion = Math.max(...entries.map(([key, item]: [string, any]) => item.version));
            const latestPipeline = entries.find(([key, item]: [string, any]) => item.version === latestVersion);
            return latestPipeline ? (latestPipeline[1] as any).id : "latest";
        }
        return pipelineID;
    }, [dataVersions]);

    const displayPipelineID = useMemo(
        () => getDisplayPipelineID(currentPipelineID),
        [currentPipelineID, getDisplayPipelineID]
    );

    const displayComparePipelineID = useMemo(
        () => getDisplayPipelineID(comparePipelineID),
        [comparePipelineID, getDisplayPipelineID]
    );

    // Handler for pipeline version change
    const handlePipelineChange = useCallback((value: number | "latest") => {
        setCurrentPipelineID(value);
        const params = new URLSearchParams(location.search);

        if (value === "latest") {
            params.delete('pipeline_version');
        } else {
            if (dataVersions) {
                const entries = Object.entries(dataVersions);
                const pipeline = entries.find(([, item]: [string, any]) => item.id === value);
                if (pipeline) {
                    params.set('pipeline_version', (pipeline[1] as any).version.toString());
                }
            }
        }

        const newSearch = params.toString();
        const newURL = `/#${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.history.pushState({}, '', newURL);
    }, [location.search, location.pathname, dataVersions]);

    const triggerPackData = useCallback((newConfig: any) => {
        const verb_trigger = `service_item/${service_item_id}/${KEY_STAGE[current]}/`;
        dataProvider.createAction(RESOURCE_PACK_DATA, { data: newConfig, verb: verb_trigger })
            .then((response: any) => {notify('AI Processor is triggered'); handlePipelineChange("latest"); refreshAll()})
            .catch((error: any) => notify(JSON.stringify(error.body), 'error'));
    }, [service_item_id, current, dataProvider, notify, handlePipelineChange, refreshAll]);

    const retriggerAIProcessor = useCallback(() => {
        const verb_trigger = `retrigger/service_item/${service_item_id}/`;
        let inputValue = '';

        Modal.confirm({
            title: 'Service Owner feedback:',
            icon: null,
            width: ModalWidth,
            style: { height: '70vh' },
            content:
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', height: '100%' } },
                    React.createElement('div', null, 'This feedback will be given to the AI model as context for the next loop'),
                    React.createElement(Input.TextArea, {
                        rows: 8,
                        style: { flex: 1 },
                        placeholder: 'Your comment',
                        onChange: (e: any) => { inputValue = e.target.value; }
                    })
                ),
            onOk() {
                let payload = undefined;
                if(inputValue !== "")
                    payload = { serviceowner_comment: inputValue };
                dataProvider.createAction(RESOURCE_PACK, { data: payload, verb: verb_trigger })
                    .then((response: any) => {notify('AI Processor is triggered'); handlePipelineChange("latest"); refreshAll()})
                    .catch((error: any) => notify(JSON.stringify(error.body), 'error'));
            }
        });
    }, [service_item_id, dataProvider, notify, handlePipelineChange, refreshAll]);

    const triggerAIProcessor = useCallback(() => {
        const verb_trigger = `trigger/service_item/${service_item_id}/${KEY_STAGE[current]}/`;
        dataProvider.createAction(RESOURCE_PACK, { data: undefined, verb: verb_trigger })
            .then((response: any) => {notify('AI Processor is triggered'); handlePipelineChange("latest"); refreshAll()})
            .catch((error: any) => notify(JSON.stringify(error.body), 'error'));
    }, [service_item_id, current, dataProvider, notify, handlePipelineChange, refreshAll]);

    const toggleAppliedStatus = useCallback(() => {
        const newAppliedStatus = !dataPipeline?.applied;

        dataProvider.update(RESOURCE_PIPELINE, {
            previousData: { id: dataPipeline.id },
            id: dataPipeline.id, data: { applied: newAppliedStatus }
        })
        .then((response: any) => {
            notify(`Pipeline marked as ${newAppliedStatus ? 'applied' : 'not applied'}`);
            refreshAll();
        })
        .catch((error: any) => notify(JSON.stringify(error.body), 'error'));
    }, [dataPipeline, dataProvider, notify, refreshAll]);

    return {
        current,
        setCurrent,
        isEditing,
        setIsEditing,
        compareMode,
        setCompareMode,
        comparePipelineID,
        setComparePipelineID,

        currentPackData,
        comparePackData,
        pipelineOptions,
        displayPipelineID,
        displayComparePipelineID,

        dataPipeline,
        dataComparePipeline,

        currentAIProcessorResponse,
        compareAIProcessorResponse,
        genUIData,

        handlePipelineChange,
        triggerPackData,
        triggerAIProcessor,
        retriggerAIProcessor,
        toggleAppliedStatus,
    };
};

export default usePackPipeline;
